import type { ReactNode } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface DailyLogDetailPanelsProps {
  isMdUp: boolean;
  manpowerCount: number;
  deliveriesCount: number;
  delaysCount: number;
  photosCount: number;
  renderOverview: () => ReactNode;
  renderWeather: () => ReactNode;
  renderManpower: () => ReactNode;
  renderDeliveries: () => ReactNode;
  renderDelays: () => ReactNode;
  renderPhotos: () => ReactNode;
  renderNotes: () => ReactNode;
}

function tabCountBadge(count: number) {
  if (count <= 0) return null;
  return (
    <Badge variant="secondary" className="ml-1.5 text-xs">
      {count}
    </Badge>
  );
}

export function DailyLogDetailPanels({
  isMdUp,
  manpowerCount,
  deliveriesCount,
  delaysCount,
  photosCount,
  renderOverview,
  renderWeather,
  renderManpower,
  renderDeliveries,
  renderDelays,
  renderPhotos,
  renderNotes,
}: DailyLogDetailPanelsProps) {
  if (isMdUp) {
    return (
      <Tabs defaultValue="overview" className="w-full overflow-visible">
        <TabsList className="w-full overflow-x-auto justify-start h-auto flex-wrap gap-1">
          <TabsTrigger value="overview" className="min-h-[40px]">
            Overview
          </TabsTrigger>
          <TabsTrigger value="weather" className="min-h-[40px]">
            Weather
          </TabsTrigger>
          <TabsTrigger value="manpower" className="min-h-[40px]">
            Manpower
            {tabCountBadge(manpowerCount)}
          </TabsTrigger>
          <TabsTrigger value="deliveries" className="min-h-[40px]">
            Deliveries
            {tabCountBadge(deliveriesCount)}
          </TabsTrigger>
          <TabsTrigger value="delays" className="min-h-[40px]">
            Delays
            {tabCountBadge(delaysCount)}
          </TabsTrigger>
          <TabsTrigger value="photos" className="min-h-[40px]">
            Photos
            {tabCountBadge(photosCount)}
          </TabsTrigger>
          <TabsTrigger value="notes" className="min-h-[40px]">
            Notes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          {renderOverview()}
        </TabsContent>
        <TabsContent value="weather" className="mt-4 overflow-visible">
          {renderWeather()}
        </TabsContent>
        <TabsContent value="manpower" className="mt-4">
          {renderManpower()}
        </TabsContent>
        <TabsContent value="deliveries" className="mt-4">
          {renderDeliveries()}
        </TabsContent>
        <TabsContent value="delays" className="mt-4 overflow-visible">
          {renderDelays()}
        </TabsContent>
        <TabsContent value="photos" className="mt-4">
          {renderPhotos()}
        </TabsContent>
        <TabsContent value="notes" className="mt-4">
          {renderNotes()}
        </TabsContent>
      </Tabs>
    );
  }

  return (
    <Accordion
      type="multiple"
      defaultValue={["overview"]}
      className="w-full rounded-lg border bg-card px-1 shadow-sm"
    >
      <AccordionItem value="overview" className="border-b px-3">
        <AccordionTrigger className="py-3 text-base font-semibold hover:no-underline">
          Overview
        </AccordionTrigger>
        <AccordionContent>{renderOverview()}</AccordionContent>
      </AccordionItem>
      <AccordionItem value="weather" className="border-b px-3">
        <AccordionTrigger className="py-3 text-base font-semibold hover:no-underline">
          Weather
        </AccordionTrigger>
        <AccordionContent className="overflow-visible">{renderWeather()}</AccordionContent>
      </AccordionItem>
      <AccordionItem value="manpower" className="border-b px-3">
        <AccordionTrigger className="py-3 text-base font-semibold hover:no-underline">
          <span className="flex items-center gap-2">
            Manpower
            {tabCountBadge(manpowerCount)}
          </span>
        </AccordionTrigger>
        <AccordionContent>{renderManpower()}</AccordionContent>
      </AccordionItem>
      <AccordionItem value="deliveries" className="border-b px-3">
        <AccordionTrigger className="py-3 text-base font-semibold hover:no-underline">
          <span className="flex items-center gap-2">
            Deliveries
            {tabCountBadge(deliveriesCount)}
          </span>
        </AccordionTrigger>
        <AccordionContent>{renderDeliveries()}</AccordionContent>
      </AccordionItem>
      <AccordionItem value="delays" className="border-b px-3">
        <AccordionTrigger className="py-3 text-base font-semibold hover:no-underline">
          <span className="flex items-center gap-2">
            Delays
            {tabCountBadge(delaysCount)}
          </span>
        </AccordionTrigger>
        <AccordionContent className="overflow-visible">{renderDelays()}</AccordionContent>
      </AccordionItem>
      <AccordionItem value="photos" className="border-b px-3">
        <AccordionTrigger className="py-3 text-base font-semibold hover:no-underline">
          <span className="flex items-center gap-2">
            Photos
            {tabCountBadge(photosCount)}
          </span>
        </AccordionTrigger>
        <AccordionContent>{renderPhotos()}</AccordionContent>
      </AccordionItem>
      <AccordionItem value="notes" className="border-b-0 px-3">
        <AccordionTrigger className="py-3 text-base font-semibold hover:no-underline">
          Notes
        </AccordionTrigger>
        <AccordionContent>{renderNotes()}</AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
